/**
 * Picks one field instance per field template when a form has more than one for the same template.
 *
 * Duplicate sets have been created in the past by a repair step running against a corrupted load (see
 * Diverging-Cells-Issue.md). Until the data is cleaned up, the renderer used to key fields by template id, so whichever
 * duplicate happened to come last in the array won and the populated one could be hidden. This makes the choice
 * deterministic and data-driven, without writing anything:
 *  - tables / photo logs: the instance with the most rows in fieldsOrder, then the oldest
 *  - everything else: an instance with a value (or an image), then the oldest
 * Instances without any template id are passed through untouched.
 */

export interface SelectableFieldInstance {
	id: string;
	fieldTemplateId?: string | null;
	fieldTemplate?: { id?: string | null; inputType?: string | null } | null;
	fieldsOrder?: string[] | null;
	value?: string | null;
	imageId?: string | null;
	createdAt?: string | null;
}

export interface PrimaryFieldInstanceSelection<T extends SelectableFieldInstance> {
	/** The instances to render, in their original order */
	selected: T[];
	/** Template id → ids of the instances that were not selected, only for templates that had duplicates */
	duplicates: Record<string, string[]>;
}

function templateIdOf(fieldInstance: SelectableFieldInstance): string | null {
	return fieldInstance.fieldTemplate?.id || fieldInstance.fieldTemplateId || null;
}

function isTable(fieldInstance: SelectableFieldInstance): boolean {
	const inputType = fieldInstance.fieldTemplate?.inputType;
	return inputType === "table" || inputType === "imageroll";
}

function rowCount(fieldInstance: SelectableFieldInstance): number {
	return Array.isArray(fieldInstance.fieldsOrder) ? fieldInstance.fieldsOrder.length : 0;
}

function hasContent(fieldInstance: SelectableFieldInstance): boolean {
	return !!fieldInstance.value || !!fieldInstance.imageId;
}

/**
 * Returns true when `candidate` should be preferred over `current` for the same template.
 */
function isBetter(candidate: SelectableFieldInstance, current: SelectableFieldInstance): boolean {
	if (isTable(candidate) || isTable(current)) {
		const candidateRows = rowCount(candidate);
		const currentRows = rowCount(current);
		if (candidateRows !== currentRows) return candidateRows > currentRows;
	} else {
		const candidateHasContent = hasContent(candidate);
		const currentHasContent = hasContent(current);
		if (candidateHasContent !== currentHasContent) return candidateHasContent;
	}
	// Tie: the older instance is the original one. Equal timestamps fall back to the id so the pick never depends on
	// the order the instances were loaded in (the merge and the export lambda use the same total order).
	const candidateCreated = candidate.createdAt || "";
	const currentCreated = current.createdAt || "";
	if (candidateCreated !== currentCreated) return candidateCreated < currentCreated;
	return candidate.id < current.id;
}

export function selectPrimaryFieldInstances<T extends SelectableFieldInstance>(fieldInstances: T[]): PrimaryFieldInstanceSelection<T> {
	const winners = new Map<string, T>();
	const losers: Record<string, string[]> = {};

	for (const fieldInstance of fieldInstances) {
		const templateId = templateIdOf(fieldInstance);
		if (!templateId) continue;
		const current = winners.get(templateId);
		if (!current) {
			winners.set(templateId, fieldInstance);
		} else if (isBetter(fieldInstance, current)) {
			(losers[templateId] ||= []).push(current.id);
			winners.set(templateId, fieldInstance);
		} else {
			(losers[templateId] ||= []).push(fieldInstance.id);
		}
	}

	const selected = fieldInstances.filter((fieldInstance) => {
		const templateId = templateIdOf(fieldInstance);
		if (!templateId) return true;
		return winners.get(templateId) === fieldInstance;
	});

	return { selected, duplicates: losers };
}

export default selectPrimaryFieldInstances;
